import useTeamContext from '@maxpreps/shared/contexts/TeamContext';
import CardIncAdUnit from '@maxpreps/shared/corecomponents/AdUnit/CardIncAdUnit';
import useIsAdmin from '@maxpreps/shared/hooks/useIsAdmin';
import {
  useIsDesktop,
  useIsMobile
} from '@maxpreps/shared/hooks/useIsScreenSize';
import dynamic from 'next/dynamic';
import React, { useMemo } from 'react';
import { ITeamMetaPageProps } from '../../utils/ITeamPageConfig';
import getRecordCardProps from '../../utils/schedule/getRecordCardProps';
import useIsUserATeamAdmin from '../../utils/useIsUserATeamAdmin';
import ContributionCard from '../shared/ContributionCard';
import RecordCard from '../shared/RecordCard/RecordCard';
import TeamPageLayout from '../shared/TeamPageLayout';
import ScheduleCallouts from './ScheduleCallouts/ScheduleCallouts';
import useScheduleContext from './ScheduleContext';
import ScheduleHeaderActions from './ScheduleHeaderActions';
import ScheduleLinksContainer from './ScheduleLinksContainer';
import ScheduleTable from './ScheduleTable';

const AdminSubheaderSchedule = dynamic(
  () => import('../shared/Admin/AdminSubheader/AdminSubheaderSchedule'),
  {
    ssr: false
  }
);

const AdminAccessCard = dynamic(
  () => import('../shared/Admin/AdminAccessCard'),
  {
    ssr: false
  }
);

const ScheduleReport = dynamic(
  () => import('../shared/Admin/ReportCard/ScheduleReport/ScheduleReport'),
  {
    ssr: false
  }
);

const SchedulePage: React.FC<ITeamMetaPageProps> = ({ pageConfig }) => {
  const isAdmin = useIsAdmin();
  const isMobile = useIsMobile();
  const isDesktop = useIsDesktop();

  const isUserATeamAdmin = useIsUserATeamAdmin();
  const scheduleContext = useScheduleContext();
  const teamContext = useTeamContext();

  const { standings, leagueStandings, scheduleCallouts } = scheduleContext;

  const { leagueCanonicalUrl } = teamContext.data;

  const aboveCard = useMemo<JSX.Element | undefined>(
    () =>
      isUserATeamAdmin ? (
        <>
          <ScheduleCallouts key="0" scheduleCallouts={scheduleCallouts} />
          <ScheduleReport
            key="1"
            isSchedulePage={true}
            pageConfig={pageConfig}
          />
        </>
      ) : undefined,
    [isUserATeamAdmin, scheduleCallouts, pageConfig]
  );

  const belowCard =
    !isDesktop && isUserATeamAdmin ? <AdminAccessCard /> : undefined;

  // Display contribute card on desktop for non-admins
  const rightRail = (
    <>
      {!isUserATeamAdmin && !isMobile && <ContributionCard />}
      {isDesktop && isUserATeamAdmin && <AdminAccessCard />}
    </>
  );

  const recordCardProps = getRecordCardProps(standings, leagueStandings);
  recordCardProps.leagueCanonicalUrl = leagueCanonicalUrl;

  const headerText = isAdmin ? 'Manage Schedule' : undefined;

  return (
    <TeamPageLayout
      childrenAboveCard={aboveCard}
      childrenBelowCard={belowCard}
      childrenRightRailDesktopOnly={rightRail}
      headerActions={<ScheduleHeaderActions />}
      headerText={headerText}
      pageConfig={pageConfig}
      isContributeCardManagedByParent
    >
      {isAdmin && <AdminSubheaderSchedule />}
      {/*@ts-ignore*/}
      <RecordCard {...recordCardProps} />
      <CardIncAdUnit $marginBottom="20px" incIndex={1} />
      <ScheduleTable />
      <ScheduleLinksContainer />
    </TeamPageLayout>
  );
};

export default SchedulePage;
